import { normalizeAccountCode } from './accountCode.js'

/**
 * CSIU import — REQUIREMENTS.md §6.4, workflow §7.3.
 *
 * Everything here is derived from `reference/example-csiu-export.xlsx`, a real
 * 108-row Travel export covering July 2025 to May 2026. Where the reference
 * file and the spec disagree, the file wins: it is the format contract.
 *
 * The single most surprising number in that file: **89 of 108 rows are P-Card
 * and get skipped**. An import is mostly discard, and the review screen has to
 * make that look deliberate rather than broken.
 */

/** Columns as CSIU names them. Only the ones the importer reads. */
export interface CsiuRow {
  AccountCode?: string | null
  TransactionNumber?: string | null
  /** Excel serial number, or a date string. */
  TransactionDate?: string | number | null
  Amount?: string | number | null
  Description?: string | null
  VendorName?: string | null
  PONumber?: string | null
  PaymentNumber?: string | null
  InvoiceNumber?: string | null
  JournalNumber?: string | null
}

/**
 * A row after parsing, with everything the classifier needs.
 */
export interface ParsedCsiuRow {
  /** Canonical hyphen-delimited account code, or null if unparseable. */
  accountCode: string | null
  transactionNumber: string
  /** ISO 8601 UTC at midnight, or null. */
  transactionDate: string | null
  /** Signed dollars. Negative is a reversal or credit. */
  amount: number
  description: string
  /** From `VendorName`, else recovered from the description. */
  vendor: string | null
  journalNumber: string
  poNumber: string | null
  invoiceNumber: string | null
  /** Index in the source file, for stable ordering and error messages. */
  rowNumber: number
}

/**
 * The P-Card skip rule — §6.4 step 2.
 *
 * P-Card charges are captured locally at purchase time, so importing them
 * would double-count. The reference file spells this rule out the hard way:
 * the same concept appears as `Apr26P-Card`, `Feb26PCard`, `March26PCard`,
 * `PCardJanuary2026`, `PCardEntryJuly25`, and `Nov25P-CardImp`. Hyphen
 * sometimes, space never, and the label sits anywhere in the string — so the
 * match is a substring search with an optional separator, not an equality test
 * or a prefix.
 */
const PCARD_PATTERN = /p[\s-]?card/i

export function isPCardRow(journalNumber: string | null | undefined): boolean {
  return PCARD_PATTERN.test(journalNumber ?? '')
}

/**
 * Excel's day-zero. Serial 1 is 1900-01-01, and Excel wrongly believes 1900
 * was a leap year, so counting from 1899-12-30 lands correctly for every date
 * this system will ever see.
 */
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30)
const MS_PER_DAY = 86_400_000

/**
 * Read a CSIU date into an ISO instant at midnight UTC.
 *
 * Dates arrive as Excel serials — `46142`, not `2026-04-30`. Left as a number
 * it is meaningless; parsed as a date string it becomes 1970. The legacy
 * system shipped exactly this bug: `reference/example-packet.xlsx` row 9
 * carries `46173.734178240746`, a serial *with a time fraction*, printed into
 * a date column. So the fraction is truncated here rather than rounded: a
 * quarter past eleven at night is still that calendar day, and rounding would
 * silently move it to the next one.
 */
export function parseCsiuDate(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value).trim())) {
    const serial = Number(value)
    // Excel serials for real dates are far above this; anything smaller is a
    // count, an ID, or a stray number in the wrong column.
    if (!Number.isFinite(serial) || serial < 1000) return null
    const days = Math.floor(serial)
    return new Date(EXCEL_EPOCH_MS + days * MS_PER_DAY).toISOString()
  }

  const text = String(value).trim()

  // A bare `YYYY-MM-DD` is parsed as UTC midnight by the language, and reading
  // it back with local getters lands on the previous day anywhere west of
  // Greenwich — which is everywhere this runs. Take the parts directly rather
  // than round-tripping through a timezone.
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (isoDate !== null) {
    const [, year, month, day] = isoDate
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toISOString()
  }

  // Anything else — `4/30/2026` and friends — is parsed as *local* midnight,
  // so the local getters are the correct ones to read it back with.
  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()),
  ).toISOString()
}

/** Amounts arrive as numbers or as strings with currency decoration. */
export function parseCsiuAmount(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null

  const cleaned = String(value).trim().replace(/[$,\s]/g, '')
  // Accounting notation: (123.45) means negative.
  const negated = /^\((.*)\)$/.exec(cleaned)
  const numeric = Number(negated?.[1] ?? cleaned)
  if (!Number.isFinite(numeric)) return null
  return negated === null ? numeric : -numeric
}

/**
 * Recover the merchant when `VendorName` is blank.
 *
 * P-Card rows carry no vendor code and put the merchant in the description as
 * `"United - FLIGHT TO KCK (MATT)"`. Those rows are skipped, but the same
 * shape appears on other codes, and a row with no vendor at all is far harder
 * to reconcile against a statement later.
 *
 * Only the first segment is taken, and only when it looks like a name rather
 * than a sentence — `"JUN-25 CONF. EXP REIMBURSEMENT"` has a hyphen too, and
 * splitting it would produce a vendor called "JUN".
 */
export function vendorFromDescription(description: string): string | null {
  const [head, ...rest] = description.split(' - ')
  if (head === undefined || rest.length === 0) return null

  const candidate = head.trim()
  if (candidate === '') return null
  // A merchant is short and has letters. Four words is generous for a name and
  // still excludes a description that happens to contain " - ".
  if (candidate.split(/\s+/).length > 4) return null
  if (!/[a-z]/i.test(candidate)) return null
  return candidate
}

/** A row that could not be parsed, with enough of it to find in the file. */
export interface CsiuRowProblem {
  rowNumber: number
  /** Enough of the row for a person to recognise it. */
  row: string
  reason: string
}

/**
 * Read a whole file's rows, keeping the ones that failed.
 *
 * `parseCsiuRow` returns null for a row whose amount cannot be read, and a
 * caller that merely filters those away makes the row *disappear*: the review
 * screen's "108 rows, 89 skipped" then reconciles perfectly against a file
 * that had 109. This module's whole premise is that discarding most of a file
 * should look deliberate, so a row that cannot be imported is reported rather
 * than dropped — the same way `parseBudgetGrid` reports its problems.
 */
export function parseCsiuRows(
  rows: readonly CsiuRow[],
  firstRowNumber: number,
): { parsed: ParsedCsiuRow[]; problems: CsiuRowProblem[] } {
  const parsed: ParsedCsiuRow[] = []
  const problems: CsiuRowProblem[] = []

  rows.forEach((row, index) => {
    const rowNumber = firstRowNumber + index
    const result = parseCsiuRow(row, rowNumber)
    if (result !== null) {
      parsed.push(result)
      return
    }
    const raw = String(row.Amount ?? '').trim()
    const describedBy =
      [row.VendorName, row.Description, row.TransactionNumber, row.AccountCode]
        .map((value) => String(value ?? '').trim())
        .find((value) => value !== '') ?? `row ${rowNumber}`
    problems.push({
      rowNumber,
      row: describedBy,
      reason:
        raw === ''
          ? 'Has no amount, so it cannot be imported.'
          : `"${raw}" is not an amount, so this row cannot be imported.`,
    })
  })

  return { parsed, problems }
}

export function parseCsiuRow(row: CsiuRow, rowNumber: number): ParsedCsiuRow | null {
  const amount = parseCsiuAmount(row.Amount)
  if (amount === null) return null

  const description = String(row.Description ?? '').trim()
  const vendorName = String(row.VendorName ?? '').trim()

  return {
    accountCode: normalizeAccountCode(String(row.AccountCode ?? '')),
    transactionNumber: String(row.TransactionNumber ?? '').trim(),
    transactionDate: parseCsiuDate(row.TransactionDate),
    amount,
    description,
    vendor: vendorName !== '' ? vendorName : vendorFromDescription(description),
    journalNumber: String(row.JournalNumber ?? '').trim(),
    poNumber: emptyToNull(row.PONumber),
    invoiceNumber: emptyToNull(row.InvoiceNumber),
    rowNumber,
  }
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = String(value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

// ── Reversal pairing ─────────────────────────────────────────────────────────

/** §6.4 step 3: matching amounts of opposite sign, same vendor and description. */
const REVERSAL_WINDOW_DAYS = 30

export interface ReversalPair {
  charge: ParsedCsiuRow
  reversal: ParsedCsiuRow
}

/**
 * Pair each credit with the charge it reverses.
 *
 * **One-to-one, and that is the whole difficulty.** The reference file has a
 * −$364.08 "Hershey Lodge - LODGING" credit and *two* identical +$364.08
 * charges. A naive match would claim both, cancelling a charge that was never
 * refunded and quietly understating what was spent. Each charge is therefore
 * consumed once, nearest date first, so a second identical credit pairs with
 * the second charge or with nothing at all.
 */
export function pairReversals(rows: readonly ParsedCsiuRow[]): {
  pairs: ReversalPair[]
  unpaired: ParsedCsiuRow[]
} {
  const credits = rows.filter((r) => r.amount < 0)
  const claimed = new Set<number>()
  const pairs: ReversalPair[] = []
  const unpaired: ParsedCsiuRow[] = []

  for (const credit of credits) {
    const candidates = rows
      .filter(
        (row) =>
          !claimed.has(row.rowNumber) &&
          row.rowNumber !== credit.rowNumber &&
          row.amount === -credit.amount &&
          row.description === credit.description &&
          (row.vendor ?? '') === (credit.vendor ?? '') &&
          withinDays(row.transactionDate, credit.transactionDate, REVERSAL_WINDOW_DAYS),
      )
      .sort(
        (a, b) =>
          dayGap(a.transactionDate, credit.transactionDate) -
          dayGap(b.transactionDate, credit.transactionDate),
      )

    const charge = candidates[0]
    if (charge === undefined) {
      // A credit with nothing to cancel is a real refund against an earlier
      // period, not a reversal. It still belongs in the ledger.
      unpaired.push(credit)
      continue
    }
    claimed.add(charge.rowNumber)
    claimed.add(credit.rowNumber)
    pairs.push({ charge, reversal: credit })
  }

  return { pairs, unpaired }
}

function dayGap(a: string | null, b: string | null): number {
  if (a === null || b === null) return Number.POSITIVE_INFINITY
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) / MS_PER_DAY
}

function withinDays(a: string | null, b: string | null, days: number): boolean {
  return dayGap(a, b) <= days
}

// ── Matching against what we already have ────────────────────────────────────

/** §6.4 step 4: exact by `sourceRef`, else amount + vendor + date within 31 days. */
const MATCH_WINDOW_DAYS = 31

export type RowClassification =
  | 'skip_pcard'
  | 'reversal_pair'
  | 'duplicate_exact'
  | 'duplicate_potential'
  | 'new'

export interface ExistingTransaction {
  id: string
  sourceRef: string | null
  amount: number
  vendor: string
  transactionDate: string
  status: string
}

export interface ClassifiedRow {
  row: ParsedCsiuRow
  classification: RowClassification
  /** The transaction this row matched, when it matched one. */
  matchedTransactionId?: string
  /** The row this one reverses, for a reversal pair. */
  reversesRowNumber?: number
  /** Shown in the review screen so a judgement call can be checked. */
  reason: string
}

/**
 * Classify every row of one file — §6.4 step 5.
 *
 * Order matters. P-Card is decided first because those rows never reach the
 * ledger at all, so nothing else about them is worth computing; then reversal
 * pairs, which are internal to the file; then matches against the ledger.
 */
export function classifyRows(
  rows: readonly ParsedCsiuRow[],
  existing: readonly ExistingTransaction[],
): ClassifiedRow[] {
  const importable = rows.filter((row) => !isPCardRow(row.journalNumber))
  const { pairs } = pairReversals(importable)

  const inPair = new Map<number, number>()
  for (const { charge, reversal } of pairs) {
    inPair.set(charge.rowNumber, reversal.rowNumber)
    inPair.set(reversal.rowNumber, charge.rowNumber)
  }

  // Each existing transaction may absorb only one row, for the same reason
  // reversal pairing is one-to-one: two identical charges are two charges.
  const claimed = new Set<string>()

  return rows.map((row) => {
    if (isPCardRow(row.journalNumber)) {
      return {
        row,
        classification: 'skip_pcard',
        reason: `Journal ${row.journalNumber} is a P-Card entry — already captured at purchase.`,
      }
    }

    // An exact match is checked *before* reversal pairing, and the order is
    // load-bearing. Re-importing the same export is a normal thing to do — a
    // download gets repeated, or a month's files overlap — and a row that is
    // already in the ledger is already there whether or not it belongs to a
    // pair. Labelling it a reversal first meant re-importing created both
    // halves again: balances still netted to zero, so nothing looked wrong,
    // while the ledger quietly grew four entries for two real events.
    const exact = existing.find(
      (t) =>
        !claimed.has(t.id) &&
        t.sourceRef !== null &&
        t.sourceRef !== '' &&
        t.sourceRef === row.transactionNumber,
    )
    if (exact !== undefined) {
      claimed.add(exact.id)
      return {
        row,
        classification: 'duplicate_exact',
        matchedTransactionId: exact.id,
        reason: `Already recorded — transaction number ${row.transactionNumber}.`,
      }
    }

    const partner = inPair.get(row.rowNumber)
    if (partner !== undefined) {
      return {
        row,
        classification: 'reversal_pair',
        reversesRowNumber: partner,
        reason:
          row.amount < 0
            ? 'Reverses an earlier charge in this file.'
            : 'Reversed by a credit in this file.',
      }
    }

    const potential = existing.find(
      (t) =>
        !claimed.has(t.id) &&
        Math.round(t.amount * 100) === Math.round(row.amount * 100) &&
        vendorsLookAlike(t.vendor, row.vendor) &&
        withinDays(t.transactionDate, row.transactionDate, MATCH_WINDOW_DAYS),
    )
    if (potential !== undefined) {
      claimed.add(potential.id)
      return {
        row,
        classification: 'duplicate_potential',
        matchedTransactionId: potential.id,
        reason: `Looks like the ${potential.vendor} charge already recorded — same amount, within ${MATCH_WINDOW_DAYS} days.`,
      }
    }

    return { row, classification: 'new', reason: 'Not seen before.' }
  })
}

/**
 * Words that identify a legal entity rather than a merchant. Two vendors
 * sharing only "services" or "inc" have nothing in common.
 */
const CORPORATE_NOISE = new Set([
  'inc',
  'llc',
  'llp',
  'ltd',
  'corp',
  'corporation',
  'company',
  'co',
  'the',
  'and',
  'of',
  'services',
  'service',
  'capital',
  'group',
  'holdings',
  'international',
  'usa',
  'com',
])

/** Distinctive words in a vendor name, lowercased. */
function vendorTokens(name: string): Set<string> {
  return new Set(
    name
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length >= 4 && !CORPORATE_NOISE.has(token)),
  )
}

/**
 * Do these two names plausibly describe the same vendor?
 *
 * Compared by distinctive word, not as whole strings, because the two sides
 * genuinely disagree about names. CSIU records the payee it cuts cheques to;
 * a captured receipt records the merchant. The case that forced this:
 * **"AMAZON CAPITAL SERVICES, INC." against "Amazon.com"** — neither contains
 * the other, so whole-string comparison matched nothing and every Amazon
 * purchase would have imported as new alongside the receipt already captured
 * for it. That is the double-count this importer exists to prevent, and it
 * would have appeared only on the codes that actually carry Amazon orders.
 *
 * Sharing one distinctive word is deliberately weak evidence. It is never
 * enough on its own: the caller also requires an exact amount and a date
 * within 31 days, and the result is only ever offered as *potential*, for the
 * user to confirm.
 */
function vendorsLookAlike(a: string, b: string | null): boolean {
  if (b === null) return false

  const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const left = fold(a)
  const right = fold(b)
  if (left === '' || right === '') return false
  if (left === right || left.includes(right) || right.includes(left)) return true

  const leftTokens = vendorTokens(a)
  for (const token of vendorTokens(b)) {
    if (leftTokens.has(token)) return true
  }
  return false
}

/** Totals for the review screen header. */
export function summarize(classified: readonly ClassifiedRow[]) {
  const count = (kind: RowClassification) =>
    classified.filter((c) => c.classification === kind).length

  return {
    total: classified.length,
    skipped: count('skip_pcard'),
    reversalPairs: count('reversal_pair'),
    exactDuplicates: count('duplicate_exact'),
    potentialDuplicates: count('duplicate_potential'),
    fresh: count('new'),
  }
}
